import { useState } from "react";

import type { TenantUserOut } from "@/api/tenants";
import { Button } from "@/components/ui";
import { PasswordResetModal } from "@/features/kullanicilar/PasswordResetModal";
import { t } from "@/i18n";
import { statusLabel } from "@/lib/labels";

interface MembersTableProps {
  members: TenantUserOut[];
  onChangeRole: (userId: number, role: "tenant_admin" | "tenant_user") => void;
  onRemove: (userId: number) => void;
  removeError: string | null;
  removingUserId: number | null;
}

/** Aktif/reddedilmis uyeler tablosu. Silme IKI ADIMLI (window.confirm YERINE
 * inline onay) — hem test edilebilir hem jsdom'da confirm() "not
 * implemented" uyarisi vermez. 409 (son yonetici korumasi) mesaji backend'in
 * KENDI Turkce metniyle satirin altinda gosterilir. */
export function MembersTable({ members, onChangeRole, onRemove, removeError, removingUserId }: MembersTableProps) {
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  // Şifre sıfırlama modalı (görev C) — satır başına, yalnız bir tanesi açık
  // olabilir; plaintext şifre yalnız bu modalın kendi state'inde tutulur.
  const [passwordTarget, setPasswordTarget] = useState<TenantUserOut | null>(null);

  if (members.length === 0) {
    return <p className="text-sm text-muted">{t("kullanicilar.empty")}</p>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="tbl min-w-[520px]">
          <thead>
            <tr>
              <th>{t("kullanicilar.table.email")}</th>
              <th>{t("kullanicilar.table.name")}</th>
              <th>{t("kullanicilar.table.role")}</th>
              <th>{t("kullanicilar.table.status")}</th>
              <th>{t("kullanicilar.table.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.user_id} className="align-top">
                <td>{member.email}</td>
                <td>
                  {member.first_name} {member.last_name}
                </td>
                <td>
                  <select
                    aria-label={`${t("kullanicilar.table.role")} — ${member.email}`}
                    value={member.role}
                    onChange={(e) => onChangeRole(member.user_id, e.target.value as "tenant_admin" | "tenant_user")}
                    className="input !w-auto"
                  >
                    <option value="tenant_user">{t("kullanicilar.role.tenant_user")}</option>
                    <option value="tenant_admin">{t("kullanicilar.role.tenant_admin")}</option>
                  </select>
                </td>
                <td>{statusLabel(member.status)}</td>
                <td>
                  <div className="mb-1">
                    <Button type="button" variant="outline" size="sm" onClick={() => setPasswordTarget(member)}>
                      {t("kullanicilar.password.action")}
                    </Button>
                  </div>
                  {confirmingId === member.user_id ? (
                    <div className="flex flex-col gap-1">
                      <p className="text-xs text-ink">{t("kullanicilar.remove.confirm", { email: member.email })}</p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          disabled={removingUserId === member.user_id}
                          onClick={() => {
                            onRemove(member.user_id);
                            setConfirmingId(null);
                          }}
                        >
                          {t("kullanicilar.remove")}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmingId(null)}>
                          {t("common.cancel")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button type="button" variant="danger" size="sm" onClick={() => setConfirmingId(member.user_id)}>
                      {t("kullanicilar.remove")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {removeError && confirmingId === null && (
          <p role="alert" className="px-3 py-2 text-xs text-red">
            {removeError}
          </p>
        )}
      </div>

      {passwordTarget && (
        <PasswordResetModal
          userId={passwordTarget.user_id}
          email={passwordTarget.email}
          onClose={() => setPasswordTarget(null)}
        />
      )}
    </>
  );
}
